import type { CSSProperties } from "react";

// Design System v3 — Typographie Typold, port web.
//
// ## Le changement structurant
// Sur natif, une graisse = une FAMILLE ("Typold-Bold"), parce qu'Android ignore
// `fontWeight` sur les polices custom. Sur web, @font-face porte les graisses
// (cf. styles/fonts.css) : une seule famille `Typold` et un `fontWeight`
// numérique. C'est le fonctionnement normal du CSS, et ça débloque au passage
// les utilitaires Tailwind `font-medium` / `font-bold`.
//
// `fontFamily` est donc absent des variantes ci-dessous : il est posé une fois
// sur `body` (cf. index.css). Ne le réintroduire nulle part.
//
// Règle inchangée : tout texte passe par <AppText variant="..." />.

export const fontWeight = {
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
  extraBold: 800,
} as const;

// Ancienne API, conservée en table de correspondance pour le portage du Lot 6 :
// un `fontFamily: fontFamily.bold` croisé dans un écran natif se traduit par
// `fontWeight: fontWeight.bold`.
export const fontFamily = {
  regular: fontWeight.regular,
  medium: fontWeight.medium,
  semiBold: fontWeight.semiBold,
  bold: fontWeight.bold,
  extraBold: fontWeight.extraBold,
} as const;

// Les tailles natives étaient en points, sans unité. En CSS il faut une unité :
// on garde `px` plutôt que `rem` pour rester au pixel près sur le design
// existant. Un passage en rem (respect du zoom texte navigateur) est un
// chantier d'accessibilité à part entière, pas un effet de bord de migration.
const px = (n: number) => `${n}px`;

export const typography = {
  // ── Display & headings ─────────────────────────────────────────────
  display: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(52),
    letterSpacing: px(-1.82),
    lineHeight: px(56),
  },
  h1: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(28),
    letterSpacing: px(-0.98),
    lineHeight: px(34),
  },
  h2: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(22),
    letterSpacing: px(-0.44),
    lineHeight: px(28),
  },
  h3: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(18),
    letterSpacing: px(-0.18),
    lineHeight: px(24),
  },
  h4: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(17),
    lineHeight: px(22),
  },
  subheading: {
    fontWeight: fontWeight.semiBold,
    fontSize: px(17),
    letterSpacing: px(-0.17),
    lineHeight: px(22),
  },

  // ── Body (15px) ────────────────────────────────────────────────────
  body: {
    fontWeight: fontWeight.medium,
    fontSize: px(15),
    lineHeight: px(22),
  },
  bodyBold: {
    fontWeight: fontWeight.bold,
    fontSize: px(15),
    lineHeight: px(22),
  },
  bodyStrong: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(15),
    lineHeight: px(22),
  },

  // ── Label (14px — libellés, valeurs, montants tx) ──────────────────
  label: {
    fontWeight: fontWeight.medium,
    fontSize: px(14),
    lineHeight: px(20),
  },
  labelBold: {
    fontWeight: fontWeight.bold,
    fontSize: px(14),
    lineHeight: px(20),
  },
  labelStrong: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(14),
    lineHeight: px(20),
  },

  // ── Small (13px) ───────────────────────────────────────────────────
  caption: {
    fontWeight: fontWeight.regular,
    fontSize: px(13),
    lineHeight: px(19),
  },
  small: {
    fontWeight: fontWeight.medium,
    fontSize: px(13),
    lineHeight: px(19),
  },
  smallBold: {
    fontWeight: fontWeight.bold,
    fontSize: px(13),
    lineHeight: px(19),
  },
  smallStrong: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(13),
    lineHeight: px(19),
  },

  // ── Mini (12px) ────────────────────────────────────────────────────
  mini: {
    fontWeight: fontWeight.medium,
    fontSize: px(12),
    lineHeight: px(17),
  },
  miniBold: {
    fontWeight: fontWeight.bold,
    fontSize: px(12),
    lineHeight: px(17),
  },
  link: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(12),
    lineHeight: px(16),
  },

  // ── Tiny (11px) ────────────────────────────────────────────────────
  tiny: {
    fontWeight: fontWeight.medium,
    fontSize: px(11),
    lineHeight: px(16),
  },
  tinyBold: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(11),
    lineHeight: px(16),
  },

  // ── Eyebrows (uppercase) ───────────────────────────────────────────
  eyebrow: {
    fontWeight: fontWeight.bold,
    fontSize: px(11),
    letterSpacing: px(1.54),
    lineHeight: px(16),
    textTransform: "uppercase",
  },
  eyebrowStrong: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(11),
    letterSpacing: px(1.2),
    lineHeight: px(16),
    textTransform: "uppercase",
  },
  eyebrowSm: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(9),
    letterSpacing: px(0.6),
    lineHeight: px(12),
    textTransform: "uppercase",
  },

  // ── Montants numériques ────────────────────────────────────────────
  // `tabular-nums` : chaque chiffre occupe la même largeur → un solde qui
  // passe de 111 111 à 999 999 ne fait plus « danser » la mise en page, et
  // les colonnes de montants s'alignent (règle Base pour toute donnée
  // numérique). Sans effet si la fonte n'expose pas la feature (fallback sûr).
  // Le `fontVariant: ["tabular-nums"]` de RN devient `fontVariantNumeric`.
  amountXl: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(56),
    letterSpacing: px(-2),
    lineHeight: px(60),
    fontVariantNumeric: "tabular-nums",
  },
  amountLg: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(44),
    letterSpacing: px(-1.5),
    lineHeight: px(48),
    fontVariantNumeric: "tabular-nums",
  },
  amountMd: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(40),
    letterSpacing: px(-1),
    lineHeight: px(44),
    fontVariantNumeric: "tabular-nums",
  },
  amountSm: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(22),
    letterSpacing: px(-0.5),
    lineHeight: px(28),
    fontVariantNumeric: "tabular-nums",
  },
  // Montant en corps de liste (lignes de transactions, récaps) : `label` +
  // chiffres tabulaires, pour des colonnes qui s'alignent.
  labelAmount: {
    fontWeight: fontWeight.bold,
    fontSize: px(14),
    lineHeight: px(20),
    fontVariantNumeric: "tabular-nums",
  },

  // ── CTA ────────────────────────────────────────────────────────────
  button: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(16),
    letterSpacing: px(0.2),
    lineHeight: px(20),
  },

  // ── Spécifique ─────────────────────────────────────────────────────
  numpad: {
    fontWeight: fontWeight.extraBold,
    fontSize: px(26),
    lineHeight: px(30),
  },
} as const satisfies Record<string, CSSProperties>;

export type TypographyVariant = keyof typeof typography;
